import { useCallback, useSyncExternalStore } from 'react';

import type { CommunityPost } from '~/interfaces/community';

type OptimisticPatches = Record<string, Partial<CommunityPost>>;

let optimisticPatches: OptimisticPatches = {};
const listeners = new Set<() => void>();

const emitChange = () => {
  for (const listener of listeners) {
    listener();
  }
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = () => optimisticPatches;

export const useCommunityPostOptimistic = () => {
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const mergePost = useCallback((post: CommunityPost): CommunityPost => {
    const patch = optimisticPatches[post.id];
    if (!patch) return post;
    return { ...post, ...patch };
  }, []);

  const applyOptimisticPatch = useCallback((id: string, patch: Partial<CommunityPost>) => {
    optimisticPatches = { ...optimisticPatches, [id]: { ...optimisticPatches[id], ...patch } };
    emitChange();
  }, []);

  const clearOptimisticPatch = useCallback((id: string) => {
    if (!optimisticPatches[id]) return;
    const next = { ...optimisticPatches };
    delete next[id];
    optimisticPatches = next;
    emitChange();
  }, []);

  return { mergePost, applyOptimisticPatch, clearOptimisticPatch };
};

export const useMergedCommunityPost = (post: CommunityPost): CommunityPost => {
  const { mergePost } = useCommunityPostOptimistic();
  return mergePost(post);
};
